import React, { useEffect, useState } from 'react';
import { camelCase, isEmpty, pick } from 'lodash';

import { Alert, Spacer } from '@zapier/design-system';
import { Spacing } from '@zapier/theme';

import CheckboxField from 'app/common/components/CheckboxField';
import DropdownInputField from 'app/developer-v3/components/DropdownInputField';
import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import FieldMessage, {
  FieldType,
} from 'app/developer-v3/components/FieldMessage';
import FlowForm from 'app/common/components/FlowForm';
import FormaticConfig from 'app/developer-v3/FormaticConfig';
import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB, NEW_SERVICE_FIELD } from 'app/developer-v3/constants';
import { useCurrentVersionReadOnlyReason } from 'app/developer-v3/hooks';
import {
  getInputField,
  getTriggerUrl,
  normalizeField,
  normalizeFormFields,
} from 'app/developer-v3/utils';

import type { ErrorList } from 'app/developer-v3/types/validator';
import type {
  DropdownType,
  FormFieldTemplate,
  FormifiedFieldSchema,
  InfoMeta,
  TriggerParams,
} from 'app/developer-v3/types';
import type { StaticDropdown } from 'app/developer-v3/platformSchema/field';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';

type Props = {
  attemptedSave: () => boolean;
  errors: ErrorList;
  formFields: FormFieldTemplate[];
  formifiedField: FormifiedFieldSchema;
  isFormValid: boolean;
  info?: InfoMeta;
  isSaveDisabled: (props: {}) => boolean;
  isSaving: boolean;
  onFormChange: (form: FormifiedFieldSchema) => void;
  availableTriggers: TriggerSchema[];
  params: TriggerParams & { inputKey: string };
  resetUnsavedEdits: () => void;
  saveTriggerChanges: () => void;
  setDropdown: (type: DropdownType, dropdown: string | StaticDropdown) => void;
  setInputField: (inputKey: string) => void;
  toggleDropdown: (isDropdownEnabled: boolean) => void;
  trigger: TriggerSchema;
};

const TriggerInputEditor = (props: Props) => {
  const {
    attemptedSave,
    errors,
    formifiedField,
    formFields,
    isFormValid,
    isSaveDisabled,
    isSaving,
    params,
    onFormChange,
    availableTriggers,
    resetUnsavedEdits,
    saveTriggerChanges,
    setDropdown,
    setInputField,
    toggleDropdown,
    trigger,
    info,
  } = props;

  const { inputKey } = params;
  const { choices, dynamic } = formifiedField;

  const [isDropdownEnabled, enableDropdown] = useState(
    inputKey === NEW_SERVICE_FIELD ? false : !!choices || !!dynamic
  );

  const isReadOnly = !!useCurrentVersionReadOnlyReason();

  useEffect(() => {
    setInputField(inputKey);
    return () => resetUnsavedEdits();
  }, [inputKey, resetUnsavedEdits, setInputField]);

  const onCheckDropdown = isChecked => {
    enableDropdown(isChecked);
    toggleDropdown(isChecked);
  };

  const formValues = normalizeField(formifiedField);
  const dropdownConfigs = pick(
    isEmpty(formValues) ? getInputField(trigger, inputKey) : formValues,
    ['dynamic', 'choices']
  );

  return (
    <EditorBodyLayout
      cancelRoute={`${getTriggerUrl(params)}/input`}
      isDisabled={isReadOnly || isSaveDisabled(props)}
      isSaving={isSaving}
      onSave={saveTriggerChanges}
    >
      <Alert status="info">
        <Link
          href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#5-7-integration-uses-english-language`}
          target="_blank"
        >
          Use English
        </Link>{' '}
        for all user-facing text.{' '}
        <Link
          href={`${BASE_DOC_URL_VB}/publish/integration-build-guidelines#trigger-input-fields`}
          target="_blank"
        >
          Learn more
        </Link>{' '}
        about trigger input fields.
      </Alert>
      <Spacer height={Spacing.medium} />
      <FlowForm
        config={FormaticConfig}
        fields={normalizeFormFields(formFields, formValues)}
        isValid={isFormValid}
        meta={{ errors: attemptedSave && errors, info }}
        onBlur={({ field }) =>
          onFormChange({ ...formifiedField, [field.key]: field.value.trim() })
        }
        onChange={onFormChange}
        value={formValues}
      />
      <React.Fragment>
        <CheckboxField
          field={{
            key: 'isDropdownEnabled',
            label: 'Dropdown',
            readOnly: isReadOnly,
            value: isDropdownEnabled,
          }}
          onChange={onCheckDropdown}
        />
        {info && info.dynamic && (
          <FieldMessage
            fieldKey="is-dropdown-enabled"
            message={info.dynamic.message}
            type={camelCase(`info ${info.dynamic.type}`) as FieldType}
          />
        )}
      </React.Fragment>
      {isDropdownEnabled && (
        <DropdownInputField
          dropdownConfigs={dropdownConfigs}
          errors={attemptedSave && errors}
          info={info}
          setDropdown={setDropdown}
          triggers={availableTriggers}
        />
      )}
    </EditorBodyLayout>
  );
};

export default TriggerInputEditor;
